// Campaign (utm_*) labels on a signup link, e.g. a partner listing's
// https://dashboard.filespin.io/signup?utm_source=canva&utm_medium=referral&utm_campaign=plugins
//
// Read straight from the URL and sent with the signup (email: POST
// /api/v1/signup; Google / Microsoft: the SSO start URL, which keeps them in
// its server-side state through the IdP round-trip). The backend credits the
// new account in folk (Latest / First known platform...) and sends Plausible a
// "Trial Signup" event tagged with them. Nothing is stored in the browser.
//
// The same six parameters and character rule as the website's forms Worker
// and Cal.com booking embed, so every channel reports the same labels.

export const UTM_PARAMS = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
  ['campaign_id', 'utm_id'],
] as const;

export type SignupCampaign = Partial<Record<(typeof UTM_PARAMS)[number][0], string>>;

const VALUE_RE = /^[\w .~:/+-]{1,200}$/;

// Only allowlisted, plain-label values; none at all without a utm_source.
export function utmParams(search: string): URLSearchParams {
  const query = new URLSearchParams(search);
  const out = new URLSearchParams();
  UTM_PARAMS.forEach(([, param]) => {
    const value = query.get(param)?.trim();
    if (value && VALUE_RE.test(value)) out.set(param, value);
  });
  return out.has('utm_source') ? out : new URLSearchParams();
}

export function campaignFromSearch(search: string): SignupCampaign | undefined {
  const utms = utmParams(search);
  if (!utms.has('utm_source')) return undefined;
  const campaign: SignupCampaign = {};
  UTM_PARAMS.forEach(([key, param]) => {
    const value = utms.get(param);
    if (value) campaign[key] = value;
  });
  return campaign;
}

// The page the visitor signed up on, without its query (the labels travel
// separately, and the query may hold things that are not ours to forward).
export function signupPage(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

// `path` with the current page's utm_* appended, so a link between the
// login and signup pages does not drop the labels.
export function withUtm(path: string, search: string): string {
  const q = utmParams(search).toString();
  return q ? `${path}${path.includes('?') ? '&' : '?'}${q}` : path;
}
